/**
 * ConversationLookup — admin conversation deep-dive tool
 * (specs/open/conversation-id-lookup.md §5), rendered as the
 * "Conversation ID Lookup" tab inside AdminPanel. No overlay/close
 * chrome of its own -- AdminPanel owns that -- this component is just
 * the tab's content, following FeedbackManagement's existing pattern.
 *
 * Given a conversationId, retrieves and renders every turn's question,
 * tool calls (including literal SQL), answer, and per-turn outcome
 * summary, so an on-call engineer can deep-dive a reported issue without
 * database/log access. Rendering follows spec §3's four-combination
 * enumeration exactly:
 *   - both manifest+artifact present: normal render, status line only
 *     for a non-COMPLETED status
 *   - manifest present, no artifact, RUNNING/RESUMING/PAUSED: status
 *     label only, no content (nothing exists yet)
 *   - manifest present, no artifact, terminal status (COMPLETED/FAILED/
 *     CANCELLED): explicit "Content unavailable for this turn" line --
 *     never a blank card (spec §3's correction; the backend can't tell
 *     expired-from-S3 apart from malformed-and-skipped, so this wording
 *     stays cause-agnostic)
 *   - artifact present, no manifest: full content shown, "Status
 *     unavailable" label (the symmetric expiry-skew case)
 * Turn numbers are rendered as-is, never renumbered -- a gap (e.g.
 * starting at turn 5) means earlier turns have expired from both stores,
 * not a bug (spec §5's explanatory-copy requirement).
 */
import React, { useState } from 'react';
import {
  fetchConversationLookup,
  FetchConversationLookupError,
  ConversationLookupTurn,
} from './fetchConversationLookup';
import { describeToolCall } from './describe-tool-call';
import { ThemeColors } from './useTheme';

interface ConversationLookupProps {
  colors: ThemeColors;
}

type LookupState =
  | { step: 'idle' }
  | { step: 'loading' }
  | { step: 'loaded'; turns: ConversationLookupTurn[] }
  | { step: 'not-found'; message: string }
  | { step: 'error'; message: string };

/** Human-readable label for a turn's manifest status, when a status line is warranted (spec §3). */
const STATUS_LABELS: Partial<Record<NonNullable<ConversationLookupTurn['status']>, string>> = {
  RUNNING: 'This turn is still in progress.',
  RESUMING: 'This turn is still in progress.',
  PAUSED: 'This turn is awaiting a reply and was never completed.',
  FAILED: 'This turn failed.',
  CANCELLED: 'This turn was cancelled.',
};

export function ConversationLookup({ colors }: ConversationLookupProps): React.ReactElement {
  const [conversationId, setConversationId] = useState('');
  const [state, setState] = useState<LookupState>({ step: 'idle' });

  async function handleRetrieve(): Promise<void> {
    // Guards against concurrent calls -- the button's own disabled={state.step
    // === 'loading'} only blocks the click path; pressing Enter repeatedly
    // (handleKeyDown below) bypasses that entirely, and firing overlapping
    // fetchConversationLookup calls risks a slower earlier response
    // resolving after a faster later one and clobbering the newer, correct
    // state with stale data (found via autosde).
    if (state.step === 'loading') return;

    // Trimming happens here, client-side, before the request URL is built
    // (spec §5 -- a backend-side trim would never help, since a
    // whitespace-padded path segment already fails the backend's shape
    // check by the time any backend trim could run).
    const trimmedId = conversationId.trim();
    if (!trimmedId) return;

    setState({ step: 'loading' });
    try {
      const turns = await fetchConversationLookup(trimmedId);
      setState({ step: 'loaded', turns });
    } catch (err) {
      if (err instanceof FetchConversationLookupError && err.status === 404) {
        setState({ step: 'not-found', message: err.message });
        return;
      }
      const message = err instanceof Error ? err.message : 'Unable to look up conversation';
      setState({ step: 'error', message });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') handleRetrieve();
  }

  return (
    <div style={{ padding: '16px 20px' }}>
      <p style={{ fontSize: '13px', color: colors.textMuted, marginTop: 0, marginBottom: '16px' }}>
        Given a conversationId a user has reported, retrieve that conversation&apos;s full turn-by-turn
        transcript — including the agent&apos;s tool calls (SQL queries, files read, knowledge base searches)
        and a summary of how each answer was produced — to debug a reported issue without needing direct
        database or log access. This only works within roughly the 28-day retention window after the
        conversation happened; a list that doesn&apos;t start at turn 1 usually means the earliest turns have
        aged past that window, not a bug in this tool.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <input
          type="text"
          value={conversationId}
          onChange={(e) => setConversationId(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Conversation ID"
          style={{
            flex: 1,
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            fontSize: '13px',
            backgroundColor: 'transparent',
            color: colors.text,
            fontFamily: 'monospace',
          }}
        />
        <button
          onClick={handleRetrieve}
          disabled={state.step === 'loading' || conversationId.trim().length === 0}
          style={{
            padding: '8px 16px',
            backgroundColor: colors.suggestionText,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: state.step === 'loading' ? 'default' : 'pointer',
            fontSize: '13px',
            opacity: state.step === 'loading' ? 0.6 : 1,
          }}
        >
          {state.step === 'loading' ? 'Retrieving...' : 'Retrieve conversation details'}
        </button>
      </div>

      {state.step === 'not-found' && (
        <div style={{ color: colors.textMuted, padding: '12px 0', fontSize: '13px' }}>{state.message}</div>
      )}

      {state.step === 'error' && (
        <div style={{ color: '#f44336', padding: '12px 0', fontSize: '13px' }}>Error: {state.message}</div>
      )}

      {state.step === 'loaded' &&
        state.turns.map((turn) => {
          // hasContent must include toolCalls/outcomeSummary, not just
          // userMessage/answer -- outcomeSummary is derived independently
          // of the S3 artifact (it comes from the manifest's memoryPointer
          // via resolveMemory on the backend), so a terminal-status turn
          // whose artifact has expired (no userMessage/answer/toolCalls)
          // can still have a real outcomeSummary. Gating on only
          // userMessage/answer would silently hide that summary behind
          // "Content unavailable for this turn" even though real data
          // exists (found via autosde -- the spec's own "no content
          // fields, because none exist" wording for this case didn't
          // anticipate outcomeSummary's independent derivation).
          const hasContent =
            turn.userMessage !== undefined ||
            turn.answer !== undefined ||
            (turn.toolCalls !== undefined && turn.toolCalls.length > 0) ||
            turn.outcomeSummary !== undefined;
          const isTerminalNoContent =
            !hasContent && (turn.status === 'COMPLETED' || turn.status === 'FAILED' || turn.status === 'CANCELLED');
          const isPending = !hasContent && (turn.status === 'RUNNING' || turn.status === 'RESUMING' || turn.status === 'PAUSED');
          // The symmetric expiry-skew case (spec §3): content present but
          // no manifest at all (status is undefined).
          const isStatusUnavailable = hasContent && turn.status === undefined;
          // A fifth combination the spec's own four-combination enumeration
          // didn't cover: no manifest (status undefined) AND no content --
          // reachable when a turn number came from the artifact side of the
          // union but the artifact's own userMessage/answer/toolCalls/
          // outcomeSummary all happen to be empty. None of isPending/
          // isTerminalNoContent (both require a defined status) or
          // isStatusUnavailable (requires hasContent) fire for this case,
          // so without an explicit fallback it renders only the "Turn {n}"
          // header -- exactly the blank card this component's own
          // "never a blank card" invariant (Decisions Log #5) exists to
          // prevent (found via autosde).
          const isUnknownEmpty = !hasContent && turn.status === undefined;

          return (
            <div
              key={turn.turnNumber}
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '10px',
              }}
            >
              <div style={{ fontSize: '11px', color: colors.textMuted, marginBottom: '6px' }}>
                Turn {turn.turnNumber}
              </div>

              {isPending && (
                <div style={{ fontSize: '13px', color: colors.textMuted }}>{STATUS_LABELS[turn.status!]}</div>
              )}

              {(isTerminalNoContent || isUnknownEmpty) && (
                <div style={{ fontSize: '13px', color: colors.textMuted }}>
                  Content unavailable for this turn.
                  {turn.status !== undefined && STATUS_LABELS[turn.status] && <> {STATUS_LABELS[turn.status]}</>}
                </div>
              )}

              {hasContent && (
                <>
                  {isStatusUnavailable && (
                    <div style={{ fontSize: '11px', color: colors.textMuted, marginBottom: '8px', fontStyle: 'italic' }}>
                      Status unavailable.
                    </div>
                  )}
                  {!isStatusUnavailable && turn.status !== 'COMPLETED' && STATUS_LABELS[turn.status!] && (
                    <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '8px' }}>
                      {STATUS_LABELS[turn.status!]}
                    </div>
                  )}

                  {turn.userMessage !== undefined && (
                    <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 600 }}>Q: </span>
                      {turn.userMessage}
                    </div>
                  )}

                  {turn.outcomeSummary !== undefined && (
                    <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '8px', fontStyle: 'italic' }}>
                      {turn.outcomeSummary}
                    </div>
                  )}

                  {turn.toolCalls !== undefined && turn.toolCalls.length > 0 && (
                    <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                      {turn.toolCalls.map((call, i) => (
                        <div
                          key={i}
                          style={{
                            marginBottom: '6px',
                            padding: '6px 8px',
                            backgroundColor: colors.codeBg,
                            // colors.codeBg is theme-independent (always dark,
                            // see useTheme.ts) -- matches App.tsx's own
                            // .markdown-body pre convention (background:
                            // colors.codeBg, color: #d4d4d4) for the same
                            // reason. Without this explicit color, light mode
                            // inherits colors.text (near-black), producing
                            // near-black text on this near-black background --
                            // invisible except when highlighted (reported bug).
                            color: '#d4d4d4',
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                          }}
                        >
                          {describeToolCall(call)}
                        </div>
                      ))}
                    </div>
                  )}

                  {turn.answer !== undefined && (
                    <div style={{ fontSize: '13px' }}>
                      <span style={{ fontWeight: 600 }}>A: </span>
                      {turn.answer}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
    </div>
  );
}
